// === ElementNode === React Flow node used by the docked CanvasWorkflowPanel
// (Phase B). One node per InteractivePlugin sub-element. Header shows the
// kind icon + label; body shows a short preview drawn from the item shape.
// Handles on the right are *per-kind*:
//   - Graded items (MCQ, MultipleChoice, …): "correct" + "wrong" outcome handles
//   - BranchingQuestion:                     one handle per alternative ("<alt.id>")
//   - EndScreen:                             no source handles (terminal)
//   - Everything else:                       a single default "next" handle
//
// This node is intentionally simpler than the modal-internal workflow nodes
// (BranchingQuestionNode, ActionNode, …) — the docked panel is a view+select
// surface for Phase B.1. Editing happens in the InteractivityStudio modal.
import React, { useRef, useState } from "react";
import i18n from "i18next";
import { Handle, Position } from "@xyflow/react";
import OutcomeActionPicker from "../workflow/OutcomeActionPicker";
import { useWorkflowEdit, DEFAULT_WORKFLOW_EDIT } from "../workflow/WorkflowEditContext";
import "./ElementNode.scss";

// Item kinds that score answers and therefore expose distinct correct / wrong
// outflows. Authoritative list matches plugins/InteractivePlugin/components/itemKinds/*.
const GRADED_KINDS = new Set([
  "MCQ",
  "MultipleChoice",
  "MultipleAnswer",
  "YesNo",
  "TrueFalse",
  "FillInBlank",
  "DragDrop",
  "Matching",
  "Ordering",
]);

// Item kinds that support an onClick trigger — visual/interactive items that
// don't grade answers but can fire actions when the learner clicks them.
const CLICKABLE_KINDS = new Set(["VisualImage", "VisualLabel"]);

// Kinds with discrete options that we expose one handle PER option for
// (botpress-style per-answer outflows). The correct/wrong outcome handles are
// still rendered below the per-option handles for backwards-compat with
// existing onAnswer.correct/onAnswer.wrong trigger entries.
const OPTION_KINDS = new Set(["MCQ", "MultipleChoice", "MultipleAnswer", "YesNo", "TrueFalse"]);

// Normalise an item's option list into [{key, label}] regardless of kind. For
// YesNo / TrueFalse we synthesise two options from the kind's label params so
// authors get a per-side handle (Yes/No, True/False). Returns [] when no
// options are resolvable so callers fall back to plain correct/wrong handles.
function getOptionsList(kind, item) {
  const params = (item && item.params) || {};
  if (kind === "MCQ" || kind === "MultipleChoice" || kind === "MultipleAnswer") {
    const raw = Array.isArray(params.options) ? params.options : item ? item.options || [] : [];
    return raw.map((o, i) => {
      if (o && typeof o === "object") {
        const key = o.key != null ? String(o.key) : String(i);
        const label = o.label || o.text || `Option ${i + 1}`;
        return { key, label };
      }
      return { key: String(i), label: String(o) };
    });
  }
  if (kind === "YesNo") {
    return [
      { key: "yes", label: params.yesLabel || "Yes" },
      { key: "no", label: params.noLabel || "No" },
    ];
  }
  if (kind === "TrueFalse") {
    return [
      { key: "true", label: params.trueLabel || "True" },
      { key: "false", label: params.falseLabel || "False" },
    ];
  }
  return [];
}

// Resolve a short, author-visible label for the correct outcome handle so the
// node communicates which option leads to the "correct" branch. Mirrors the
// resolution in buildElementGraph.outcomeEdgeLabel — kept inline here to avoid
// coupling the node component to the graph builder. Returns "" when the source
// item has no resolvable correct text, in which case callers omit the badge.
function correctOptionTextOf(kind, item) {
  const params = (item && item.params) || {};
  if (kind === "MCQ" || kind === "MultipleChoice") {
    const opts = Array.isArray(params.options) ? params.options : item ? item.options || [] : [];
    if (!opts.length) return "";
    let idx = -1;
    if (typeof params.correctAnswer !== "undefined") {
      idx = opts.findIndex(
        (o) => (o && typeof o === "object" ? o.key : o) === params.correctAnswer
      );
    }
    if (idx < 0 && typeof params.correctIndex === "number") idx = params.correctIndex;
    if (idx < 0 || idx >= opts.length) return "";
    const c = opts[idx];
    return (c && typeof c === "object" ? c.label || c.text : String(c)) || "";
  }
  if (kind === "TrueFalse") {
    return params.correctAnswer === true
      ? params.trueLabel || "True"
      : params.falseLabel || "False";
  }
  if (kind === "YesNo") {
    return params.correctAnswer === true ? params.yesLabel || "Yes" : params.noLabel || "No";
  }
  return "";
}

// Author-facing display labels for sub-item kinds. The data layer keeps the
// authoritative PascalCase kind (item.kind === "MultipleAnswer") — this map
// only controls how the kind reads in the node header so it doesn't render as
// "MULTIPLEANSWER" (the old CSS text-transform: uppercase made the PascalCase
// drift loudly cosmetic). Unknown kinds fall back to the raw string.
const KIND_LABEL = {
  MultipleAnswer: "Multiple Answer",
  MultipleChoice: "Multiple Choice",
  FlipCard: "Flip Card",
  YesNo: "Yes / No",
  TrueFalse: "True / False",
  FillInBlank: "Fill in the Blank",
  DragDrop: "Drag & Drop",
  BranchingQuestion: "Branching Question",
  EndScreen: "End Screen",
  Matching: "Matching",
  Ordering: "Ordering",
  MCQ: "MCQ",
  DialogCards: "Dialog Cards",
  MemoryGame: "Memory Game",
  FindTheWords: "Find the Words",
  PersonalityQuiz: "Personality Quiz",
  CoursePresentation: "Course Presentation",
  SpeakTheWords: "Speak the Words",
  VisualLabel: i18n.t("ItemProps.Kind.VisualLabel") || "Görsel Etiket",
  VisualImage: i18n.t("ItemProps.Kind.VisualImage") || "Görsel",
};

// Per-kind Material icon — kept inline so the node has no external icon
// dependency. Falls back to a generic "category" glyph for unknown kinds.
const KIND_ICON = {
  FlipCard: "flip_to_back",
  MCQ: "radio_button_checked",
  MultipleChoice: "radio_button_checked",
  MultipleAnswer: "check_box",
  YesNo: "thumbs_up_down",
  TrueFalse: "rule",
  FillInBlank: "edit",
  DragDrop: "drag_indicator",
  Matching: "compare_arrows",
  Ordering: "swap_vert",
  BranchingQuestion: "call_split",
  EndScreen: "flag",
  DialogCards: "style",
  MemoryGame: "extension",
  FindTheWords: "search",
  PersonalityQuiz: "psychology",
  CoursePresentation: "slideshow",
  SpeakTheWords: "mic",
  VisualLabel: "title",
  VisualImage: "image",
};

// Pull a short, kind-appropriate preview string from the item shape. The
// item's authoritative content lives in pluginToolbarsById[boxId].state.items[i]
// — different kinds use different top-level/params fields, so the lookup is
// kind-aware. Returns "" when nothing readable is available.
function previewOf(kind, item) {
  if (!item) return "";
  const params = item.params || {};
  if (kind === "FlipCard") return params.front || item.front || "";
  // BranchingQuestion content can land at item.params (canonical) or at the
  // item top level (seedInteractivePluginBoxAndAddItem auto-create flow). Read
  // both so the preview shows up regardless of shape.
  if (kind === "BranchingQuestion") return params.prompt || item.prompt || "";
  if (kind === "EndScreen") return params.title || item.title || "";
  if (kind === "VisualLabel") return params.text || item.text || "";
  if (kind === "VisualImage") return params.imageUrl || item.imageUrl || "";
  return params.prompt || item.prompt || "";
}

function truncate(text, max = 60) {
  if (!text) return "";
  const str = String(text);
  return str.length <= max ? str : `${str.slice(0, max - 1)}…`;
}

export default function ElementNode({ data, selected }) {
  const kind = (data && data.kind) || "";
  const item = (data && data.item) || null;
  const boxId = (data && data.boxId) || null;
  const itemId = (data && data.itemId) || null;
  const allItems = (data && data.allItems) || [];

  // Outcome-action picker state
  const [pickerOutcome, setPickerOutcome] = useState(null); // null | "correct" | "wrong" | "click"
  const correctChipRef = useRef(null);
  const wrongChipRef = useRef(null);
  const clickChipRef = useRef(null);

  const { appendOutcomeAction, appendClickAction } = useWorkflowEdit();

  const handleOutcomePick = (outcome, action) => {
    if (outcome === "click") {
      const providerMounted =
        appendClickAction && appendClickAction !== DEFAULT_WORKFLOW_EDIT.appendClickAction;
      if (providerMounted) {
        appendClickAction(boxId, itemId, action);
        return;
      }
      if (typeof window !== "undefined" && window.__appendClickAction) {
        window.__appendClickAction(boxId, itemId, action);
      }
      return;
    }
    const providerMounted =
      appendOutcomeAction && appendOutcomeAction !== DEFAULT_WORKFLOW_EDIT.appendOutcomeAction;
    if (providerMounted) {
      appendOutcomeAction(boxId, itemId, outcome, action);
      return;
    }
    if (typeof window !== "undefined" && window.__appendOutcomeAction) {
      window.__appendOutcomeAction(boxId, itemId, outcome, action);
    }
  };
  // BranchingQuestion's canonical shape stores alternatives at
  // params.alternatives (see defaultParamsFor + BranchingQuestionItem) but
  // older / partially-migrated content sometimes hangs them off the item
  // directly. Read both so the docked panel renders correctly regardless.
  const alternatives =
    kind === "BranchingQuestion"
      ? (item && item.params && item.params.alternatives) || (item && item.alternatives) || []
      : [];

  // Correct/wrong branch handles only render when the item has at least one
  // configured outcome action. Empty items stay as simple cards; authors add
  // actions via the + chips in the node body, after which branches appear.
  const params = (item && item.params) || {};
  const hasOutcomeActions =
    (Array.isArray(params.onCorrectActions) && params.onCorrectActions.length > 0) ||
    (Array.isArray(params.onWrongActions) && params.onWrongActions.length > 0) ||
    !!params.onCorrectShowItemId ||
    !!params.onWrongShowItemId;

  const isGraded = GRADED_KINDS.has(kind);
  // Show correct/wrong handles + edge labels only when actions exist.
  const showOutcomes = isGraded && hasOutcomeActions;
  // Always show + chips on graded items so authors can configure outcomes.
  const showOutcomeChips = isGraded;
  const isTerminal = kind === "EndScreen";
  const hasAlts = alternatives.length > 0;
  // Round 25 Item 0: per-option handles for kinds with discrete options
  // (botpress-style). Rendered ABOVE the correct/wrong handles; the latter
  // are preserved for backwards-compat with existing onAnswer.* triggers.
  const optionList = OPTION_KINDS.has(kind) ? getOptionsList(kind, item) : [];
  const hasOptionHandles = optionList.length > 0;

  // onClick trigger for visual (non-graded, non-branching) items.
  const isClickable = CLICKABLE_KINDS.has(kind);
  const hasClickActions =
    isClickable && Array.isArray(params.onClickActions) && params.onClickActions.length > 0;

  // The default "next" handle is for plain forward items only. Branching
  // owns its outflow via per-alt handles; graded items split into
  // correct/wrong; EndScreen is terminal; clickable items get onClick handle.
  const showNext = !isTerminal && !showOutcomes && !hasAlts && !hasClickActions;
  const icon = KIND_ICON[kind] || "category";
  const kindLabel = KIND_LABEL[kind] || kind;
  const preview = truncate(previewOf(kind, item));
  // BEM-style kind modifier lets _scss apply per-kind palettes
  // (BranchingQuestion amber, EndScreen coral). Plain kinds inherit the
  // default Studio primary tint.
  const classes = ["canvas-wf-node", `canvas-wf-node--${kind}`];
  if (selected) classes.push("canvas-wf-node--selected");
  // Round 27 review #5: items materialised by the AI workflow architect get
  // a dashed border + corner "AI" badge so authors can tell at a glance which
  // sub-items came from generation vs. were authored manually. The source flag
  // lives on the item shape (item.source === "ai") and is written by the
  // workflow-architect instantiate-scenes step.
  const isAiSource = !!(item && item.source === "ai");
  if (isAiSource) classes.push("canvas-wf-node--ai");
  const altCount = Math.max(1, alternatives.length);
  // Short text shown next to the correct/wrong handles so the author sees which
  // option drives each outflow without opening the InteractivityStudio modal.
  // Falls back to the bare outcome word when the option text isn't resolvable.
  const correctText = showOutcomes ? correctOptionTextOf(kind, item) : "";

  return (
    <div className={classes.join(" ")} data-kind={kind} data-source={isAiSource ? "ai" : undefined}>
      <Handle id="in" type="target" position={Position.Left} />
      {isAiSource && (
        <span className="canvas-wf-node__ai-badge" title="Generated by AI">
          AI
        </span>
      )}
      <div className="canvas-wf-node__head">
        <i className="material-icons">{icon}</i>
        <span className="canvas-wf-node__kind">{kindLabel}</span>
      </div>
      <div className="canvas-wf-node__body">
        {hasAlts ? (
          <ul className="canvas-wf-node__alts">
            {alternatives.map((alt, i) => (
              <li
                key={alt.id || `alt-${i}`}
                className="canvas-wf-node__alt"
                data-alt-id={alt.id || `alt-${i}`}
                title={alt.label || `Option ${i + 1}`}
              >
                {truncate(alt.label || `Option ${i + 1}`, 28)}
              </li>
            ))}
          </ul>
        ) : hasOptionHandles ? (
          // Round 25 Item 0: show the option text in the body so authors see
          // which option corresponds to each opt-<key> handle on the right.
          <>
            {preview && <div className="canvas-wf-node__prompt">{preview}</div>}
            <ul className="canvas-wf-node__opts">
              {optionList.map((opt) => (
                <li
                  key={opt.key}
                  className="canvas-wf-node__opt"
                  data-opt-key={opt.key}
                  title={opt.label}
                >
                  {truncate(opt.label, 28)}
                </li>
              ))}
            </ul>
          </>
        ) : (
          preview
        )}
      </div>
      {showNext && (
        <>
          <Handle id="next" type="source" position={Position.Right} />
          <span className="canvas-wf-node__handle-label canvas-wf-node__handle-label--next">
            next
          </span>
        </>
      )}
      {hasClickActions && (
        <>
          <Handle id="onClick" type="source" position={Position.Right} style={{ top: "50%" }} />
          <span
            className="canvas-wf-node__handle-label canvas-wf-node__handle-label--click"
            style={{ top: "50%" }}
          >
            onClick
          </span>
        </>
      )}
      {isClickable && (
        <>
          <button
            ref={clickChipRef}
            type="button"
            className="canvas-wf-node__outcome-chip canvas-wf-node__outcome-chip--click nodrag"
            style={{ top: hasClickActions ? "50%" : "50%" }}
            onClick={(e) => {
              e.stopPropagation();
              setPickerOutcome((prev) => (prev === "click" ? null : "click"));
            }}
            title={i18n.t("ItemProps.AddClickAction") || "Tıklanınca eylem ekle"}
            aria-label={i18n.t("ItemProps.AddClickAction") || "Tıklanınca eylem ekle"}
          >
            +
          </button>

          {pickerOutcome === "click" && (
            <OutcomeActionPicker
              anchorEl={clickChipRef.current}
              outcome="click"
              allItems={allItems}
              onPick={(action) => handleOutcomePick("click", action)}
              onClose={() => setPickerOutcome(null)}
            />
          )}
        </>
      )}
      {hasOptionHandles &&
        optionList.map((opt, i) => {
          // Spread per-option handles across the upper ~60% of the card so
          // the correct/wrong handles still have room below them.
          const top = `${20 + ((i + 0.5) * 50) / optionList.length}%`;
          return (
            <React.Fragment key={`opt-${opt.key}`}>
              <Handle
                id={`opt-${opt.key}`}
                type="source"
                position={Position.Right}
                style={{ top }}
              />
              <span
                className="canvas-wf-node__handle-label canvas-wf-node__handle-label--opt"
                data-opt-key={opt.key}
                style={{ top }}
                title={opt.label}
              >
                {truncate(opt.label, 14)}
              </span>
            </React.Fragment>
          );
        })}
      {showOutcomes && (
        <>
          {/* correct/wrong are vertically split so edges don't overlap visually.
              When per-option handles are also rendered we push these below to
              avoid visual collision; otherwise keep the original 35%/70% layout. */}
          <Handle
            id="correct"
            type="source"
            position={Position.Right}
            style={{ top: hasOptionHandles ? "78%" : "35%" }}
          />
          <span
            className="canvas-wf-node__handle-label canvas-wf-node__handle-label--correct"
            style={{ top: hasOptionHandles ? "78%" : "35%" }}
            title={correctText ? `${correctText} (correct)` : "correct"}
          >
            {truncate(correctText ? `${correctText} (correct)` : "correct", 22)}
          </span>
          <Handle
            id="wrong"
            type="source"
            position={Position.Right}
            style={{ top: hasOptionHandles ? "90%" : "70%" }}
          />
          <span
            className="canvas-wf-node__handle-label canvas-wf-node__handle-label--wrong"
            style={{ top: hasOptionHandles ? "90%" : "70%" }}
          >
            wrong
          </span>
        </>
      )}

      {/* + chips always rendered for graded items so authors can configure outcomes.
          When actions exist (showOutcomes), chips sit next to the handles.
          When no actions yet, chips float at fixed positions inviting configuration. */}
      {showOutcomeChips && (
        <>
          <button
            ref={correctChipRef}
            type="button"
            className="canvas-wf-node__outcome-chip canvas-wf-node__outcome-chip--correct nodrag"
            style={{ top: showOutcomes ? (hasOptionHandles ? "78%" : "35%") : "35%" }}
            onClick={(e) => {
              e.stopPropagation();
              setPickerOutcome((prev) => (prev === "correct" ? null : "correct"));
            }}
            title={i18n.t("ItemProps.AddCorrectAction") || "Doğru cevap için eylem ekle"}
            aria-label={i18n.t("ItemProps.AddCorrectAction") || "Doğru cevap için eylem ekle"}
          >
            +
          </button>
          <button
            ref={wrongChipRef}
            type="button"
            className="canvas-wf-node__outcome-chip canvas-wf-node__outcome-chip--wrong nodrag"
            style={{ top: showOutcomes ? (hasOptionHandles ? "90%" : "70%") : "65%" }}
            onClick={(e) => {
              e.stopPropagation();
              setPickerOutcome((prev) => (prev === "wrong" ? null : "wrong"));
            }}
            title={i18n.t("ItemProps.AddWrongAction") || "Yanlış cevap için eylem ekle"}
            aria-label={i18n.t("ItemProps.AddWrongAction") || "Yanlış cevap için eylem ekle"}
          >
            +
          </button>

          {(pickerOutcome === "correct" || pickerOutcome === "wrong") && (
            <OutcomeActionPicker
              anchorEl={pickerOutcome === "correct" ? correctChipRef.current : wrongChipRef.current}
              outcome={pickerOutcome}
              allItems={allItems}
              onPick={(action) => handleOutcomePick(pickerOutcome, action)}
              onClose={() => setPickerOutcome(null)}
            />
          )}
        </>
      )}
      {hasAlts &&
        alternatives.map((alt, i) => {
          const top = `${28 + ((i + 0.5) * 65) / altCount}%`;
          const labelText = alt.label || `Option ${i + 1}`;
          return (
            <React.Fragment key={alt.id || `alt-${i}`}>
              <Handle
                id={alt.id || `alt-${i}`}
                type="source"
                position={Position.Right}
                style={{ top }}
              />
              <span
                className="canvas-wf-node__handle-label canvas-wf-node__handle-label--alt"
                data-alt-id={alt.id || `alt-${i}`}
                style={{ top }}
                title={labelText}
              >
                {truncate(labelText, 22)}
              </span>
            </React.Fragment>
          );
        })}
    </div>
  );
}
